/**
 * Radio Component
 * Themed radio input with gradient support
 */

import { forwardRef } from 'react';
import { Radio as MantineRadio, RadioGroup as MantineRadioGroup } from '@mantine/core';
import type { RadioProps, RadioGroupProps } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import type { GradientVariant } from '../../../theme/types';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ThemedRadioProps extends RadioProps {
  gradientVariant?: GradientVariant;
  gradientCheck?: boolean; // Apply gradient to checked radio
}

export interface ThemedRadioGroupProps extends RadioGroupProps {
  gradientVariant?: GradientVariant;
}

/**
 * Radio input component with gradient support
 *
 * @example
 * ```tsx
 * <Radio
 *   value="option1"
 *   label="Option 1"
 *   gradientVariant="primary"
 *   gradientCheck
 * />
 * ```
 */
const Radio = forwardRef<HTMLInputElement, ThemedRadioProps>(
  ({ gradientVariant = 'primary', gradientCheck = false, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    return (
      <MantineRadio
        ref={ref}
        styles={{
          label: {
            ...accessibilityStyles,
          },
          ...(gradientCheck
            ? {
                radio: {
                  '&:checked': {
                    backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                    borderColor: gradient.from,
                  },
                },
              }
            : {}),
        }}
        {...rest}
      />
    );
  }
);

Radio.displayName = 'Radio';

/**
 * Radio group component with gradient support
 *
 * @example
 * ```tsx
 * <Radio.Group value={value} onChange={setValue}>
 *   <Radio value="1" label="Option 1" />
 *   <Radio value="2" label="Option 2" />
 * </Radio.Group>
 * ```
 */
const RadioGroup = forwardRef<HTMLDivElement, ThemedRadioGroupProps>(
  ({ gradientVariant = 'primary', ...rest }, ref) => {
    return <MantineRadioGroup ref={ref} {...rest} />;
  }
);

RadioGroup.displayName = 'RadioGroup';

// Attach Group as a property of Radio for Radio.Group API
(Radio as any).Group = RadioGroup;

export { Radio, RadioGroup };
